import { baseApi } from "@/shared/api";
import { authApi } from "@/features/auth/api";
import { decrementPendingActionsCount } from "@/features/auth";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import { isGPHPaymentReviewDetails } from "@/features/self/pending/types/pending.types";
import type {
  ContractorPayrollListResponse,
  ContractorPayrollDetailResponse,
  GPHPayment,
  ApproveGPHDto,
  MarkGPHPaidDto,
  PreviewCalculationDto,
  GPHPreviewCalculationResponse,
} from "../types";

export const contractorPayrollApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getContractorPayrollList: builder.query<ContractorPayrollListResponse, { year?: number; status?: string } | void>({
      query: (params) => ({
        url: "/api/accounting/gph-payments/",
        method: "GET",
        params: params || undefined,
        credentials: "include" as const,
      }),
      providesTags: [{ type: "GPHPayments" as const, id: "LIST" }],
    }),

    getContractorPayrollDetail: builder.query<ContractorPayrollDetailResponse, { year: number; month: number }>({
      query: ({ year, month }) => ({
        url: "/api/accounting/gph-payments/by-month/",
        method: "GET",
        params: { year, month },
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, { year, month }) => [
        { type: "GPHPayments" as const, id: `${year}-${month}` },
      ],
    }),

    approveGPH: builder.mutation<GPHPayment, { id: number; body?: ApproveGPHDto }>({
      query: ({ id, body }) => ({
        url: `/api/accounting/gph-payments/${id}/approve/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: ["GPHPayments" as const, "PendingActions", "SmetaData", "Budget"],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) =>
                item.action_required === "review_gph_payment" &&
                isGPHPaymentReviewDetails(item.details) &&
                item.details.gph_payment_id === id,
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),

    previewGPHCalculation: builder.mutation<GPHPreviewCalculationResponse, { id: number; body: PreviewCalculationDto }>({
      query: ({ id, body }) => ({
        url: `/api/accounting/gph-payments/${id}/preview-calculation/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
    }),

    markGPHPaid: builder.mutation<GPHPayment, { id: number; body?: MarkGPHPaidDto }>({
      query: ({ id, body }) => ({
        url: `/api/accounting/gph-payments/${id}/mark-paid/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: ["GPHPayments" as const, "PendingActions"],
    }),
  }),
});

export const {
  useGetContractorPayrollListQuery,
  useGetContractorPayrollDetailQuery,
  useApproveGPHMutation,
  useMarkGPHPaidMutation,
  usePreviewGPHCalculationMutation,
} = contractorPayrollApi;
